import React from 'react'
import Highcharts from 'highcharts/highstock'
import HighchartsReact from 'highcharts-react-official'
import CloseCircle from '../icons/CloseCircle'
import LoadingIndicator from './LoadingIndicator'
import ContainerHeading from '../layout/ContainerHeading'
import HelmetTitle from '../../components/layout/HelmetTitle'
import styledTheme from '../../styles/theme'
import {
  buildRangeSelector,
  buildTimezoneOptions,
  convertDegreesToIconAngle,
  formatSeries,
  formatYAxis,
} from './highchartsUtils'
import {
  attachWindDirectionRedrawHandler,
  initialiseHighcharts,
} from './highchartsSetup'

import {
  DataViewModalContainer,
  ContainerHeadingActionIcon,
  GraphContentWrapper,
  GraphContainer,
  GraphOverlayNotice,
  GraphOverlayNoticeRow,
  GraphOverlayNoticeButton,
  GraphOverlayNoticeIcon,
  GraphOverlayNoticeBody,
} from './styled-elements'

const TWO_WEEKS_IN_MS = 1_209_600_000
const RESAMPLING_WARNING_LABEL = 'Data grouping'
const RESAMPLING_WARNING_TEXT = 'Data resampling at this zoom level will result in some minimums and maximums being averaged out. Choose a zoom level of 1-week or less to avoid this.'
const MOBILE_CHART_AIDS_HIDE_LABEL = 'Hide legend'
const MOBILE_CHART_AIDS_SHOW_LABEL = 'Show legend'

export default class Graph extends React.Component {

  constructor(props) {
    super(props)
    const layoutWidth=this.getLayoutWdth()
    const layoutHeight=this.getLayoutHeight()
    const isSmallScreen=(layoutWidth < styledTheme.breakpoints.lg)
    const mobileLandscapeView=(isSmallScreen && (layoutWidth > layoutHeight))
    this.state = {
      layoutWidth,
      layoutHeight,
      isSmallScreen,
      mobileLandscapeView,
      showResamplingWarning: false,
      showResamplingWarningDetails: false,
      showMobileChartAids: !isSmallScreen,
    }
    this.graphContainerDiv = React.createRef()
    this.highChartsChart = React.createRef()
    this.chartUiDefaults = null

    initialiseHighcharts(Highcharts)

  }

  getLayoutWdth = () => {
    if (typeof window === 'undefined') return styledTheme.breakpoints.lg
    return window.innerWidth
  }

  getLayoutHeight = () => {
    if (typeof window === 'undefined') return (styledTheme.breakpoints.lg*9/16)
    return window.innerHeight
  }

  processOptions = (props,state) => {

    const mobileDisplayOptions = {}
    const options = {
      ...props.options,
      chart: {
        ...props.options.chart,
      },
    }

    const chartEvents = props.options.chart.events || {}
    const existingLoad = chartEvents.load

    options.chart.events = {
      ...chartEvents,
      load: (event) => {
        if (existingLoad) existingLoad.call(this, event)
        this.captureChartUiDefaults(event.target)
        this.syncMobileChartAids(event.target)
        this.updateResamplingWarning(event.target)
      },
      redraw: (event) => {
        this.updateResamplingWarning(event.target)
      },
    }

    attachWindDirectionRedrawHandler({
      options,
      convertDegreesToIconAngle,
    })

    const xAxis = options.xAxis[0]
    const xAxisMin = xAxis.min
    const xAxisMax = xAxis.max
    const xAxisRangeDiff = xAxisMax-xAxisMin

    if (typeof options.rangeSelector === 'undefined') {
      options.rangeSelector = buildRangeSelector(xAxisRangeDiff)
    }

    return {
      ...options,
      ...mobileDisplayOptions,
      series: formatSeries(options.series.slice()),
      yAxis: formatYAxis(options.yAxis.slice()),
      time: buildTimezoneOptions(props.site.timezone_name)
    }
  }

  isResamplingWarningVisible = (chart) => {
    if (!chart || this.props.options.type==='simple_column_chart') return false

    const xAxis = chart.xAxis && chart.xAxis[0]
    if (!xAxis || typeof xAxis.min !== 'number' || typeof xAxis.max !== 'number') return false

    return (xAxis.max - xAxis.min) >= TWO_WEEKS_IN_MS
  }

  updateResamplingWarning = (chart) => {
    const showResamplingWarning = this.isResamplingWarningVisible(chart)
    if (showResamplingWarning !== this.state.showResamplingWarning) {
      this.setState({
        showResamplingWarning,
        showResamplingWarningDetails: showResamplingWarning ? this.state.showResamplingWarningDetails : false,
      })
    }
  }

  toggleResamplingWarningDetails = () => {
    this.setState((prevState) => ({
      showResamplingWarningDetails: !prevState.showResamplingWarningDetails,
    }))
  }

  isMobileChartAidsToggleVisible = () => this.state.isSmallScreen && this.props.options.type!=='simple_column_chart'

  captureChartUiDefaults = (chart) => {
    if (!chart || this.chartUiDefaults) return

    this.chartUiDefaults = {
      legendEnabled: !!(chart.legend && chart.legend.options && chart.legend.options.enabled),
      navigatorEnabled: !!(chart.options && chart.options.navigator && chart.options.navigator.enabled !== false),
      navigatorMargin: chart.options && chart.options.navigator ? chart.options.navigator.margin : undefined,
      scrollbarEnabled: !!(chart.options && chart.options.scrollbar && chart.options.scrollbar.enabled),
      scrollbarHeight: chart.options && chart.options.scrollbar ? chart.options.scrollbar.height : undefined,
      scrollbarMargin: chart.options && chart.options.scrollbar ? chart.options.scrollbar.margin : undefined,
    }
  }

  syncMobileChartAids = (chart = this.highChartsChart.current && this.highChartsChart.current.chart) => {
    if (!chart || this.props.options.type==='simple_column_chart') return

    this.captureChartUiDefaults(chart)

    const showChartAids = !this.state.isSmallScreen || this.state.showMobileChartAids
    chart.update({
      legend: {
        enabled: showChartAids && this.chartUiDefaults.legendEnabled,
      },
      navigator: {
        ...chart.options.navigator,
        enabled: showChartAids && this.chartUiDefaults.navigatorEnabled,
        margin: showChartAids ? this.chartUiDefaults.navigatorMargin : 0,
      },
      scrollbar: {
        ...chart.options.scrollbar,
        enabled: showChartAids && this.chartUiDefaults.scrollbarEnabled,
        height: showChartAids ? this.chartUiDefaults.scrollbarHeight : 0,
        margin: showChartAids ? this.chartUiDefaults.scrollbarMargin : 0,
      },
    }, false, true, false)
    chart.redraw()
  }

  toggleMobileChartAids = () => {
    this.setState((prevState) => ({
      showMobileChartAids: !prevState.showMobileChartAids,
    }))
  }

  processSimpleColumnChartOptions = (props,state) => {

    const mobileDisplayOptions = {}
    const options = props.options

    if (state.layoutWidth < styledTheme.breakpoints.lg) {
      mobileDisplayOptions['scrollbar'] = { enabled: false }
      mobileDisplayOptions['legend'] = { enabled: false }
    }

    const xAxis = options.xAxis[0]
    const xAxisMin = xAxis.min
    const xAxisMax = xAxis.max
    let xAxisRangeDiff = xAxisMax-xAxisMin
    if (xAxisMin && xAxisMax) {
      xAxisRangeDiff = xAxisMax-xAxisMin
    } else {
      const dataTimestampArray = options.series.map(({data}) => data.map((ds) => ds[0]))
      const dataMinMaxArray = dataTimestampArray.map((tsArray) => [Math.min(...tsArray),Math.max(...tsArray)])
      const dataMinArray = dataMinMaxArray.map((minMaxItem) => Math.min(...minMaxItem))
      const dataMaxArray = dataMinMaxArray.map((minMaxItem) => Math.max(...minMaxItem))
      const rangeMin = Math.min(dataMinArray)
      const rangeMax = Math.max(dataMaxArray)
      xAxis['min'] = rangeMin
      xAxis['max'] = rangeMax
      xAxisRangeDiff = rangeMax-rangeMin
    }

    if (typeof options.rangeSelector === 'undefined') {
      options.rangeSelector = buildRangeSelector(xAxisRangeDiff, {
        allButtonsEnabled: true,
        minimumRangeForSelector: 610000000,
      })
    }

    const ret = {
      ...options,
      ...mobileDisplayOptions,
      series: formatSeries(options.series.slice()),
      yAxis: formatYAxis(options.yAxis.slice()),
      time: buildTimezoneOptions(props.site.timezone_name)
    }

    return ret

  }

  last3Heights = []
  last3Widths = []

  resizeHandler = (e) => {

    this.last3Heights = []
    this.last3Widths = []

    const resizeFunction = () => {
      if (e.target.innerWidth===this.state.layoutWidth) return
      const layoutWidth=this.getLayoutWdth()
      const layoutHeight=this.getLayoutHeight()
      const isSmallScreen=(layoutWidth < styledTheme.breakpoints.lg)
      const mobileLandscapeView=(isSmallScreen && (layoutWidth > layoutHeight))
      this.setState({
        chartContainerHeight: this.graphContainerDiv.current.offsetHeight,
        chartContainerWidth: this.graphContainerDiv.current.offsetWidth,
        layoutWidth,
        layoutHeight,
        isSmallScreen,
        mobileLandscapeView,
      })
    }

    // To handle animated screen re-sizing and delayed updating of window.innerWidth in Mobile Chrome:
    // Run this function 3 times, and only when the height and width have been stable for 3 seconds,
    // stop calling resizeFunction

    const intervalId = setInterval(() => {
      this.last3Widths.push(e.target.innerWidth)
      this.last3Heights.push(e.target.innerHeight)
      this.last3Widths = this.last3Widths.slice(-3)
      this.last3Heights = this.last3Heights.slice(-3)
      if ((this.last3Widths.length < 3) || (this.last3Heights.length < 3)) {
        resizeFunction()
      } else {
        const widthTotal = this.last3Widths.reduce((n,total) => total+n,0)
        const heightTotal = this.last3Heights.reduce((n,total) => total+n,0)
        if (((widthTotal/3)===this.last3Widths[2]) && ((heightTotal/3)===this.last3Heights[2])) {
          clearInterval(intervalId)
        } else {
          resizeFunction()
        }
      }
    },500)
  }

  addDataPoint = (seriesId,dataPoint) => {
    const chart = this.highChartsChart.current.chart
    const series = chart.get(seriesId)
    series.addPoint(dataPoint)
    const xAxis = series.xAxis
    const yAxis = series.yAxis
    xAxis.setExtremes(Math.min(xAxis.min,dataPoint[0]),Math.max(xAxis.max,dataPoint[0]))
    yAxis.setExtremes(Math.min(yAxis.min,dataPoint[1]),Math.max(yAxis.max,dataPoint[1]))
  }

  componentDidMount = () => {
    if (this.highChartsChart.current) {
      const container = this.highChartsChart.current.container.current;
      container.style.height = "100%";
      container.style.width = "100%";
      this.highChartsChart.current.chart.reflow();
      this.captureChartUiDefaults(this.highChartsChart.current.chart)
      this.syncMobileChartAids(this.highChartsChart.current.chart)
      this.updateChartContainerDimensions()
    }
    window.addEventListener('resize', this.resizeHandler)
  }

  componentWillUnmount = () => {
    window.removeEventListener('resize', this.resizeHandler)
  }

  componentDidUpdate = (prevProps, prevState) => {
    this.updateChartContainerDimensions()
    if (this.highChartsChart.current && this.highChartsChart.current.chart) {
      this.highChartsChart.current.chart.setSize(
        this.state.chartContainerWidth,
        this.state.chartContainerHeight,
      )
      if (
        prevState.isSmallScreen !== this.state.isSmallScreen ||
        prevState.showMobileChartAids !== this.state.showMobileChartAids
      ) {
        this.syncMobileChartAids(this.highChartsChart.current.chart)
      }
      this.highChartsChart.current.chart.reflow()
    }
  }

  updateChartContainerDimensions() {
    if (
      (this.graphContainerDiv) &&
      (this.graphContainerDiv.current) &&
      (
        (this.state.chartContainerHeight!==this.graphContainerDiv.current.offsetHeight) ||
        (this.state.chartContainerWidth!==this.graphContainerDiv.current.offsetWidth)
      )
    ) {
        this.setState({
          chartContainerHeight: this.graphContainerDiv.current.offsetHeight,
          chartContainerWidth: this.graphContainerDiv.current.offsetWidth,
        })
    }
  }

  renderBody = (props,state) => {

    const { site, dataView, options, fetching } = props
    const title = site.name && `${site.name}: ${dataView.name}`

    const ContainerHeadingBlock = <ContainerHeading
      title={title}
      actionIcons={
        <ContainerHeadingActionIcon onClick={this.props.closeDataViewHandler}><CloseCircle /></ContainerHeadingActionIcon>
      }
    />

    if (!Object.keys(options).length || !options.chart) {
      return <DataViewModalContainer>
        <LoadingIndicator show={fetching} />
        <HelmetTitle title={title} />
        {ContainerHeadingBlock}
      </DataViewModalContainer>
    }

    const processedOptions = () => {
      if (props.options.type==='simple_column_chart') {
        return this.processSimpleColumnChartOptions(props,state)
      } else {
        return this.processOptions(props,state)
      }
    }

    const constructorType = options.constructorType || 'stockChart'

    const HighchartsObj = <GraphContentWrapper>
      <GraphContainer ref={this.graphContainerDiv}>
        <HighchartsReact
          ref={this.highChartsChart}
          highcharts={Highcharts}
          constructorType={constructorType}
          options={processedOptions()}
          allowChartUpdate={false}
        />
        {(state.showResamplingWarning || this.isMobileChartAidsToggleVisible()) && <GraphOverlayNotice>
          <GraphOverlayNoticeRow>
            {this.isMobileChartAidsToggleVisible() && <GraphOverlayNoticeButton type="button" onClick={this.toggleMobileChartAids}>
              {state.showMobileChartAids ? MOBILE_CHART_AIDS_HIDE_LABEL : MOBILE_CHART_AIDS_SHOW_LABEL}
            </GraphOverlayNoticeButton>}
            {state.showResamplingWarning && <GraphOverlayNoticeButton type="button" onClick={this.toggleResamplingWarningDetails}>
              <GraphOverlayNoticeIcon>{'\u26A0\uFE0F'}</GraphOverlayNoticeIcon>
              {RESAMPLING_WARNING_LABEL}
            </GraphOverlayNoticeButton>}
          </GraphOverlayNoticeRow>
          {state.showResamplingWarningDetails && <GraphOverlayNoticeBody>{RESAMPLING_WARNING_TEXT}</GraphOverlayNoticeBody>}
        </GraphOverlayNotice>}
      </GraphContainer>
    </GraphContentWrapper>

    return <DataViewModalContainer>
      <LoadingIndicator show={fetching} />
      <HelmetTitle title={title} />
      {ContainerHeadingBlock}
      {HighchartsObj}
    </DataViewModalContainer>
  }

  render = () => this.renderBody(this.props,this.state)

}
